import { MicrophoneIcon } from "@heroicons/react/outline";
import { useEffect, useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useDispatch, useSelector } from "react-redux";
import { AnyAction } from "redux-saga";
import { ThunkDispatch } from "redux-thunk";
import styled, { keyframes } from "styled-components";

import Progress from "./Progress";
import getWorker from "./worker-factory";
import { setDictationGlobalStatus } from "../../actions/sysActions";
import { addToast, nextId } from "../../actions/toastActions";
import { isSuppressedString } from "../../utils/dictation";
import Spinner from "../Spinner";

const WHISPER_SAMPLING_RATE = 16_000;
const MAX_AUDIO_LENGTH = 120; // seconds
const MAX_SAMPLES = WHISPER_SAMPLING_RATE * MAX_AUDIO_LENGTH;
const browserSupported =
  !!navigator.mediaDevices.getUserMedia && !!(navigator as any).gpu;

const Wrapper = styled.div<{ $error: boolean }>`
  align-items: center;
  display: flex;
  opacity: ${(props) => (props.$error ? ".3" : "1")};
`;

const Button = styled.button<{ $active: boolean }>`
  height: 25px;
  width: 25px;
  position: relative;
  border: none;
  padding: 0;
  border-radius: 100%;
  color: ${(props) => (props.$active ? "white" : "black")};
  background-color: ${(props) => (props.$active ? "black" : "transparent")};
  display: flex;
  align-items: stretch;
  justify-content: center;
`;

const ringAnimation = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(1.4);
    opacity: 0;
  }
`;

const Ring = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 3px solid black;
  border-radius: 50%;
  animation: ${ringAnimation} 1s ease-out infinite;
`;

const Mic = styled.div`
  height: 16px;
  width: 16px;
`;

export type DictationProps = {
  dictationEnabled?: boolean;
  onDictation: Function;
  onFinish?: Function;
  shortcutEnabled?: boolean;
};

const Dictation = ({
  dictationEnabled,
  onDictation,
  onFinish,
  shortcutEnabled,
}: DictationProps) => {
  const dispatch = useDispatch() as ThunkDispatch<any, any, AnyAction>;
  const globalDictationStatus = useSelector(({ sys }) => sys.dictation.status);

  const prevDictationStatus = useRef<string>("loading");
  const prevText = useRef<string>("");
  const worker = useRef<SharedWorker | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Dictation status: loading, loaded, warming, ready, connecting, recording, finished, error
  const [dictationStatus, setDictationStatus] = useState<string>("loading");
  const [text, setText] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [chunks, setChunks] = useState<any>([]);
  const [allChunksProcessed, setAllChunksProcessed] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(false);

  const dictationLoading =
    (open && dictationStatus !== "recording") ||
    globalDictationStatus === "inactive" ||
    globalDictationStatus === "loading" ||
    dictationStatus === "submitting" ||
    dictationStatus === "connecting";

  let chunkLength = 0;

  const showErrorMessage = (message: string, removalTimeout: number) => {
    const toastId = nextId();
    dispatch(
      addToast(
        {
          kind: "danger",
          message,
          removalTimeout,
        },
        toastId
      )
    );
  };

  // Create a callback function for messages from the worker thread.
  const onMessageReceived = (e: MessageEvent) => {
    switch (e.data.status) {
      case "loading":
        // Model file start load: add a new progress item to the list.
        setDictationStatus("loading");
        break;

      case "loaded":
        // Pipeline ready: the worker is ready to warm the model.
        setDictationStatus("loaded");
        break;

      case "warming":
        // Pipeline ready: the worker is ready to warm the model.
        setDictationStatus("warming");
        break;

      case "ready":
        // Model warmed: the worker is ready to accept messages.
        setDictationStatus("ready");
        break;

      case "start":
        // Start generation
        setIsProcessing(true);

        // Request new data from the recorder if present.
        if (recorderRef.current?.state !== "inactive") {
          recorderRef.current?.requestData();
        }
        break;

      case "complete":
        const isHallucinating =
          chunkLength <= 2 && e.data.result.chunks[0].text === " you";

        setIsProcessing(false);

        if (!isHallucinating) {
          if (recorderRef.current?.state === "inactive") {
            setText("");
          } else {
            // Every tine a chunk is complete, count up the chunk length.
            // Compare that to the the number of chunks waiting to be processed.
            // If they are the same, then tell the component all chunks are processed.
            // The component will only finish submitting text when all chunks
            // are processed, even it dictation has been stopped.
            if (chunkLength !== e.data.result.chunks.length) {
              chunkLength = e.data.result.chunks.length;
              setAllChunksProcessed(false);
            } else {
              setAllChunksProcessed(true);
            }

            setText(e.data.result.text);
          }
        }
        break;

      case "finished":
        setIsProcessing(false);
        setDictationStatus("finished");
        break;
    }
  };

  // Only finish dictation if all chunks are processed, and the status is submitting.
  useEffect(() => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }

    const finishDictation = () => {
      // Stop the recorder
      recorderRef.current?.stop();
      resetDictation();
      onFinish && onFinish();
    };

    if (dictationStatus === "submitting" && allChunksProcessed) {
      finishDictation();
    } else if (dictationStatus === "recording" && allChunksProcessed) {
      timeoutIdRef.current = setTimeout(() => {
        finishDictation();
      }, 8000);
    }

    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };
  }, [allChunksProcessed]);

  // setup the worker as soon as the component is mounted.
  useEffect(() => {
    if (browserSupported && dictationEnabled) {
      if (!worker.current) {
        // Create the worker if it does not yet exist.
        worker.current = getWorker();
      }

      worker.current.port.onmessage = (event) => {
        onMessageReceived(event);
      };

      // Define a cleanup function for when the component is unmounted.
      return () => {
        worker.current?.port.postMessage({ type: "close" });
        worker.current = null;

        if (globalDictationStatus === "recording") {
          dispatch(setDictationGlobalStatus("ready"));
        }
      };
    }
  }, [globalDictationStatus]);

  // Update the display text when the text changes.
  useEffect(() => {
    if (text && text.length > 0) {
      if (text !== prevText.current) {
        prevText.current = text;

        if (!isSuppressedString(text)) {
          onDictation(text);
        }
      }
    }
  }, [text]);

  useEffect(() => {
    // In the case that the user stops dictation and restarts it, we close the port in
    // use and reopen it for performance reasons. When that happens, this effect will
    // trigger recording once the model is reloaded and port is opened, which should be
    // almost instantaneous as the model is cached.
    if (
      open &&
      prevDictationStatus.current === "warming" &&
      dictationStatus === "ready"
    ) {
      record();
    }

    prevDictationStatus.current = dictationStatus;
  }, [dictationStatus]);

  const record = () => {
    // Enable dictation button
    setActive(true);

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        // Mic is ready; start recording
        setDictationStatus("recording");
        dispatch(setDictationGlobalStatus("recording"));

        recorderRef.current = new MediaRecorder(stream);
        audioContextRef.current = new AudioContext({
          sampleRate: WHISPER_SAMPLING_RATE,
        });

        recorderRef.current.onstart = () => {
          setChunks([]);
        };

        recorderRef.current.ondataavailable = (e) => {
          if (e.data.size > 0) {
            setChunks((prev: any[]) => [...prev, e.data]);
          } else {
            // Empty chunk received, so we request new data after a short timeout
            setTimeout(() => {
              if (recorderRef.current?.state !== "inactive") {
                recorderRef.current?.requestData();
              }
            }, 25);
          }
        };

        recorderRef.current.onstop = () => {
          const tracks = stream.getTracks();
          // When all tracks have been stopped the stream will
          // no longer be active and release any permissioned input
          tracks.forEach((track) => track.stop());
        };

        recorderRef.current.start();
      })
      .catch(() => {
        setDictationStatus("loading");
        setActive(false);
        setOpen(false);
        showErrorMessage(
          "Please enable the microphone permission in your browser.",
          2000
        );
      });
  };

  // Cleanup the recorder worker on unmount
  useEffect(() => {
    return () => {
      recorderRef.current?.stop();
      recorderRef.current = null;
    };
  }, []);

  // Generate audio and post to the worker
  useEffect(() => {
    if (!recorderRef.current) return;
    if (recorderRef.current?.state === "inactive") return;
    if (isProcessing) return;
    if (
      dictationStatus !== "recording" &&
      dictationStatus !== "submitting" &&
      dictationStatus !== "connecting"
    )
      return;

    if (chunks.length > 0) {
      // Generate from data
      const blob = new Blob(chunks, { type: recorderRef.current.mimeType });

      const fileReader = new FileReader();

      fileReader.onloadend = async () => {
        const arrayBuffer = fileReader.result as ArrayBuffer;
        const decoded = await audioContextRef.current?.decodeAudioData(
          arrayBuffer
        );
        let audio = decoded && decoded.getChannelData(0);
        if (audio && audio.length > MAX_SAMPLES) {
          // Get last MAX_SAMPLES
          audio = audio.slice(-MAX_SAMPLES);
        }

        worker.current?.port.postMessage({
          type: "generate",
          data: { audio, language: "en" },
        });
      };
      fileReader.readAsArrayBuffer(blob);
    } else {
      recorderRef.current?.requestData();
    }
  }, [dictationStatus, isProcessing, chunks]);

  const resetDictation = () => {
    setOpen(false);
    setActive(false);
    setText("");
    setAllChunksProcessed(false);

    worker.current?.port.postMessage({ type: "finish" });

    dispatch(setDictationGlobalStatus("ready"));
  };

  const toggleDictation = () => {
    if (!browserSupported) {
      showErrorMessage("Browser does not support speech recognition", 2000);
    } else if (globalDictationStatus === "error") {
      showErrorMessage("Error loading dictation model.", 5000);
    } else if (dictationEnabled) {
      if (dictationStatus === "recording") {
        if (allChunksProcessed) {
          // Stop the recorder
          recorderRef.current?.stop();
          resetDictation();
          onFinish && onFinish();
        } else {
          setOpen(false);
          setActive(false);
          setDictationStatus("submitting");
        }
      } else {
        // If model is loaded and ready, record. If dictation is finished from
        // previous usage, load the model.
        if (globalDictationStatus === "ready") {
          // Start microphone connection
          setDictationStatus("connecting");
          setOpen(true);

          if (dictationStatus === "finished") {
            // Dictation waas finished, and the worker was closed.
            // Use the shared worker and add a message listener again.
            // Send the load message, but the worker will skip loading
            // the model since it is already loaded, and post a loaded event.
            worker.current = getWorker();
            worker.current.port.onmessage = (event) => {
              onMessageReceived(event);
            };
            worker.current?.port.postMessage({ type: "load" });
          } else {
            record();
          }
        } else if (globalDictationStatus === "recording") {
          showErrorMessage(
            "Dictation is already active. Turn dictation off in that location and try again.",
            5000
          );
        }
      }
    }
  };

  // Toggle dictation on/off by pressing "cmd+k".
  useHotkeys(
    "Meta+i",
    () => {
      if (shortcutEnabled && !dictationLoading) {
        toggleDictation();
      }
    },
    { enableOnFormTags: true },
    [dictationStatus, shortcutEnabled]
  );

  // Pressing esc will stop dictation and reset the submitted text
  useHotkeys(
    "Escape",
    () => {
      if (shortcutEnabled) {
        if (dictationStatus === "recording") {
          // Stop the recorder
          recorderRef.current?.stop();

          resetDictation();
        } else if (dictationLoading || globalDictationStatus === "ready") {
          resetDictation();
        }

        onFinish && onFinish();
      }
    },
    [dictationStatus]
  );

  if (!dictationEnabled) {
    return null;
  }

  return (
    <Wrapper $error={globalDictationStatus === "error"}>
      {globalDictationStatus === "loading" && <Progress />}
      <Button
        $active={open}
        onClick={(e) => {
          e.preventDefault();
          toggleDictation();
        }}
        disabled={(open && !active) || dictationLoading}
      >
        <Mic>
          {dictationLoading ? (
            <Spinner
              size="16px"
              width="3px"
              frontColor="black"
              backColor="#d8d8d8"
            />
          ) : (
            <>
              <MicrophoneIcon />
              {active && <Ring />}
            </>
          )}
        </Mic>
      </Button>
    </Wrapper>
  );
};

export default Dictation;
